import { applyStripboardDelta } from './stripboardDeltaService'
import type { StripboardDelta, StripboardDeltaResult } from '../types/stripboard'
import { markEduAttendance, type MarkEduAttendanceInput, type MarkEduAttendanceResult } from './eduAttendanceService'
import {
  uploadHomeworkFiles,
  submitEduHomework,
  type SubmitEduHomeworkInput,
  type SubmitEduHomeworkResult,
} from './eduHomeworkService'
import { isNoSignInPreviewActive } from '../debug'
import { visibilityAwareInterval } from '../utils/visibilityAwareInterval'
import { getNlsString } from '../nls/nlsRuntime'

export type MemberOutboxStatus = 'PENDING' | 'SYNCING' | 'FAILED' | 'COMPLETED' | 'EXPIRED'
export type MemberOutboxType = 'ELEMENT_STATUS_MOVE' | 'EDU_ATTENDANCE_MARK' | 'EDU_HOMEWORK_SUBMIT'

export type OutboxHomeworkFile = {
  name: string
  type: string
  blob: Blob
}

export type OutboxEduHomework = {
  input: Omit<SubmitEduHomeworkInput, 'fileIds'>
  files: OutboxHomeworkFile[]
  uploadedFileIds: string[]
}

export type MemberOutboxRecord = {
  id: string
  type: MemberOutboxType
  delta?: StripboardDelta
  eduAttendance?: MarkEduAttendanceInput
  eduHomework?: OutboxEduHomework
  status: MemberOutboxStatus
  retryCount: number
  error: string | null
  createdAt: number
  lastUpdatedClient: number
  uid?: string | null
  workspaceId?: string | null
  workspaceName?: string | null
  terminal?: boolean
}

export type MemberOutboxSnapshot = {
  pendingCount: number
  syncingCount: number
  failedIds: string[]
}

const DB_NAME = 'member-outbox'
const DB_VERSION = 1
const STORE_MUTATIONS = 'mutations'
const OUTBOX_EVENT = 'timelining:member-outbox'
export const STALE_SYNCING_MS = 120_000

const activeProcessingIds = new Set<string>()

let dbPromise: Promise<IDBDatabase> | null = null
let memoryRecords: MemberOutboxRecord[] = []

const generateId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `member-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const openDb = (): Promise<IDBDatabase> => {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('IndexedDB unavailable'))
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_MUTATIONS)) {
        const store = db.createObjectStore(STORE_MUTATIONS, { keyPath: 'id' })
        store.createIndex('status', 'status', { unique: false })
        store.createIndex('createdAt', 'createdAt', { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Member outbox open failed'))
  })
  return dbPromise
}

const withStore = async <T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_MUTATIONS, mode)
    const store = tx.objectStore(STORE_MUTATIONS)
    const request = work(store)
    tx.onerror = () => reject(tx.error ?? new Error('Member outbox transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('Member outbox transaction aborted'))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Member outbox request failed'))
  })
}

const safeListRecords = async (): Promise<MemberOutboxRecord[]> => {
  try {
    return await withStore('readonly', (store) => store.getAll() as IDBRequest<MemberOutboxRecord[]>)
  } catch {
    return [...memoryRecords]
  }
}

export const putRecord = async (record: MemberOutboxRecord) => {
  try {
    await withStore('readwrite', (store) => store.put(record))
  } catch {
    memoryRecords = [...memoryRecords.filter((entry) => entry.id !== record.id), record]
  }
}

const deleteRecord = async (id: string) => {
  try {
    await withStore('readwrite', (store) => store.delete(id))
  } catch {
    memoryRecords = memoryRecords.filter((entry) => entry.id !== id)
  }
}

const emitSnapshot = async () => {
  if (typeof window === 'undefined') return
  const records = await safeListRecords()
  window.dispatchEvent(new CustomEvent<MemberOutboxSnapshot>(OUTBOX_EVENT, {
    detail: snapshotFromRecords(records),
  }))
}

const snapshotFromRecords = (records: MemberOutboxRecord[]): MemberOutboxSnapshot => ({
  pendingCount: records.filter((record) => record.status === 'PENDING' && !record.terminal).length,
  syncingCount: records.filter((record) => record.status === 'SYNCING').length,
  failedIds: records.filter((record) => record.status === 'FAILED').map((record) => record.delta?.recordId ?? record.id),
})

export type MemberOutboxNotifier = (message: string) => void
let outboxNotifier: MemberOutboxNotifier | null = null

export const isTransientOutboxError = (err: unknown): boolean => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true
  }
  if (!err) return false
  const errorObj = err as { code?: string; message?: string; name?: string }
  const code = typeof errorObj.code === 'string' ? errorObj.code.toLowerCase() : ''
  if (code === 'unavailable' || code === 'deadline-exceeded') {
    return true
  }
  const message = typeof errorObj.message === 'string' ? errorObj.message : String(err)
  if (code === 'internal' && /fetch|network/i.test(message)) {
    return true
  }
  if (err instanceof TypeError && /failed to fetch/i.test(message)) {
    return true
  }
  if (/failed to fetch/i.test(message)) {
    return true
  }
  return false
}

export const setMemberOutboxNotifier = (notifier: MemberOutboxNotifier | null): void => {
  outboxNotifier = notifier
}

export type MembershipStatusChecker = (uid: string, workspaceId: string) => Promise<boolean> | boolean
let membershipChecker: MembershipStatusChecker | null = null

export const setMembershipStatusChecker = (checker: MembershipStatusChecker | null): void => {
  membershipChecker = checker
}

export const checkIsActiveMember = async (uid?: string | null, workspaceId?: string | null): Promise<boolean> => {
  if (!uid || !workspaceId) return false
  if (membershipChecker) {
    return await membershipChecker(uid, workspaceId)
  }
  try {
    const { doc, getDoc } = await import('firebase/firestore')
    const { firestoreDb } = await import('./firebase')
    const snap = await getDoc(doc(firestoreDb, 'users', uid, 'workspaceMemberships', workspaceId))
    if (!snap.exists()) return false
    const data = snap.data()
    return data?.status === 'ACTIVE'
  } catch {
    return false
  }
}

export const notifyMemberOutboxTerminal = (message: string): void => {
  if (outboxNotifier) {
    try {
      outboxNotifier(message)
    } catch {
      // ignore
    }
  }
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('timelining:member-outbox-terminal', { detail: { message } }))
    } catch {
      // ignore
    }
  }
}

export const clearMemberOutboxForWorkspace = async (workspaceId: string): Promise<void> => {
  const records = await safeListRecords()
  const matching = records.filter((r) => r.workspaceId === workspaceId)
  for (const r of matching) {
    await deleteRecord(r.id)
  }
  await emitSnapshot()
}

export const clearMemberOutboxForUser = async (uid?: string): Promise<void> => {
  const records = await safeListRecords()
  const matching = uid ? records.filter((r) => r.uid === uid) : records
  for (const r of matching) {
    await deleteRecord(r.id)
  }
  await emitSnapshot()
}

export const clearAllMemberOutbox = async (): Promise<void> => {
  const records = await safeListRecords()
  for (const r of records) {
    await deleteRecord(r.id)
  }
  await emitSnapshot()
}

export const expireOldMemberOutboxEntries = async (
  maxAgeMs: number,
  errorMessage = 'Update expired: device was offline longer than the allowed policy window.',
  now = Date.now(),
): Promise<number> => {
  let expiredCount = 0
  try {
    const all = await safeListRecords()
    for (const r of all) {
      if (r.status !== 'COMPLETED' && r.status !== 'EXPIRED') {
        const itemTime = r.createdAt || r.lastUpdatedClient || now
        if (now - itemTime > maxAgeMs) {
          await putRecord({
            ...r,
            status: 'EXPIRED',
            terminal: true,
            error: errorMessage,
            lastUpdatedClient: now,
          })
          expiredCount++
        }
      }
    }
    if (expiredCount > 0) {
      await emitSnapshot()
    }
  } catch {
    // ignore
  }
  return expiredCount
}

export const getMemberOutboxSnapshot = async (): Promise<MemberOutboxSnapshot> =>
  snapshotFromRecords(await safeListRecords())

export const subscribeMemberOutbox = (listener: (snapshot: MemberOutboxSnapshot) => void) => {
  let active = true
  void getMemberOutboxSnapshot().then((snapshot) => {
    if (active) listener(snapshot)
  })
  const handle = (event: Event) => listener((event as CustomEvent<MemberOutboxSnapshot>).detail)
  window.addEventListener(OUTBOX_EVENT, handle)
  return () => {
    active = false
    window.removeEventListener(OUTBOX_EVENT, handle)
  }
}

const processRecord = async (
  record: MemberOutboxRecord,
): Promise<StripboardDeltaResult | MarkEduAttendanceResult | SubmitEduHomeworkResult | null> => {
  if (activeProcessingIds.has(record.id)) {
    return null
  }
  activeProcessingIds.add(record.id)
  try {
    const syncingRecord: MemberOutboxRecord = {
      ...record,
      status: 'SYNCING',
      retryCount: record.retryCount + 1,
      lastUpdatedClient: Date.now(),
    }
    await putRecord(syncingRecord)
    await emitSnapshot()

    const currentUploadedIds: string[] = record.eduHomework?.uploadedFileIds ? [...record.eduHomework.uploadedFileIds] : []

    try {
      if (record.type === 'EDU_HOMEWORK_SUBMIT') {
        if (isNoSignInPreviewActive()) {
          await new Promise((resolve) => window.setTimeout(resolve, 220))
          await deleteRecord(record.id)
          await emitSnapshot()
          return {
            submissionId: `${record.eduHomework?.input.checklistId ?? ''}_${record.eduHomework?.input.learnerId ?? ''}`,
            status: 'SUBMITTED',
          }
        }
        if (!record.eduHomework) throw new Error('Missing eduHomework payload')
        const { input, files } = record.eduHomework

        for (let i = currentUploadedIds.length; i < files.length; i++) {
          const fileObj = files[i]
          const nativeFile = new File([fileObj.blob], fileObj.name, { type: fileObj.type })
          const [uploadedId] = await uploadHomeworkFiles({
            workspaceId: input.workspaceId,
            checklistId: input.checklistId,
            learnerId: input.learnerId,
            files: [nativeFile],
          })
          if (!uploadedId) {
            throw new Error('Upload completed without returning a file ID.')
          }
          currentUploadedIds.push(uploadedId)
          await putRecord({
            ...syncingRecord,
            eduHomework: {
              ...record.eduHomework,
              uploadedFileIds: currentUploadedIds,
            },
          })
        }

        const result = await submitEduHomework({
          ...input,
          fileIds: currentUploadedIds,
        })
        await deleteRecord(record.id)
        await emitSnapshot()
        return result
      }

      if (record.type === 'EDU_ATTENDANCE_MARK') {
        if (isNoSignInPreviewActive()) {
          await new Promise((resolve) => window.setTimeout(resolve, 220))
          await deleteRecord(record.id)
          await emitSnapshot()
          return {
            sessionId: record.eduAttendance?.seriesId ?? '',
            rollCounts: { PRESENT: 0, ABSENT: 0, LATE: 0, ON_LEAVE: 0, EXCUSED: 0 },
            markedCount: record.eduAttendance?.marks.length ?? 0,
          }
        }
        if (!record.eduAttendance) throw new Error('Missing eduAttendance payload')
        const result = await markEduAttendance(record.eduAttendance)
        await deleteRecord(record.id)
        await emitSnapshot()
        return result
      }

      if (!record.delta) throw new Error('Missing delta payload')

      if (isNoSignInPreviewActive()) {
        await new Promise((resolve) => window.setTimeout(resolve, 220))
        const debugResult: StripboardDeltaResult = {
          operationId: record.delta.operationId,
          recordId: record.delta.recordId,
          boardStatus: record.delta.destinationStatus,
          workflowStatus: record.delta.destinationStatus,
          recordVersion: record.delta.expectedVersion + 1,
          invalidDependencyNodeIds: [],
          dependencyAlertNodeIds: [],
        }
        await deleteRecord(record.id)
        await emitSnapshot()
        return debugResult
      }

      const result = await applyStripboardDelta(record.delta)
      await deleteRecord(record.id)
      await emitSnapshot()
      return result
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : 'Member mutation failed.'

      if (isTransientOutboxError(error)) {
        const pendingRecord: MemberOutboxRecord = {
          ...syncingRecord,
          status: 'PENDING',
          error: rawMessage,
          lastUpdatedClient: Date.now(),
          ...(record.type === 'EDU_HOMEWORK_SUBMIT' && record.eduHomework
            ? {
                eduHomework: {
                  ...record.eduHomework,
                  uploadedFileIds: currentUploadedIds,
                },
              }
            : {}),
        }
        await putRecord(pendingRecord)
        await emitSnapshot()
        return null
      }

      const isPermissionDenied =
        /permission-denied|permission denied|insufficient permissions/i.test(rawMessage) ||
        (error as { code?: string })?.code === 'permission-denied'

      let isRemoved = false
      if (isPermissionDenied) {
        const isMember = await checkIsActiveMember(record.uid, record.workspaceId)
        isRemoved = !isMember
      }

      const failedRecord: MemberOutboxRecord = {
        ...syncingRecord,
        status: 'FAILED',
        terminal: Boolean(isRemoved || record.terminal),
        error: rawMessage,
        lastUpdatedClient: Date.now(),
      }
      await putRecord(failedRecord)
      await emitSnapshot()

      if (isRemoved) {
        const fallbackWorkspace = getNlsString('NLSWorkspaceFallbackThisWorkspace')
        const workspaceName = record.workspaceName || fallbackWorkspace
        const toastMsg = getNlsString('NLSMutationNoLongerMember', { name: workspaceName })
        notifyMemberOutboxTerminal(toastMsg)
      }

      throw error
    }
  } finally {
    activeProcessingIds.delete(record.id)
  }
}

export const queueMemberStatusMove = async (
  delta: StripboardDelta,
  meta?: { uid?: string | null; workspaceId?: string | null; workspaceName?: string | null },
): Promise<StripboardDeltaResult | null> => {
  const now = Date.now()
  const record: MemberOutboxRecord = {
    id: generateId(),
    type: 'ELEMENT_STATUS_MOVE',
    delta,
    status: 'PENDING',
    retryCount: 0,
    error: null,
    createdAt: now,
    lastUpdatedClient: now,
    uid: meta?.uid ?? null,
    workspaceId: meta?.workspaceId ?? null,
    workspaceName: meta?.workspaceName ?? null,
  }
  await putRecord(record)
  await emitSnapshot()
  return (await processRecord(record)) as StripboardDeltaResult | null
}

export const queueMemberAttendanceMark = async (
  input: MarkEduAttendanceInput,
  meta?: { uid?: string | null; workspaceId?: string | null; workspaceName?: string | null },
): Promise<MarkEduAttendanceResult | null> => {
  const now = Date.now()
  const record: MemberOutboxRecord = {
    id: generateId(),
    type: 'EDU_ATTENDANCE_MARK',
    eduAttendance: input,
    status: 'PENDING',
    retryCount: 0,
    error: null,
    createdAt: now,
    lastUpdatedClient: now,
    uid: meta?.uid ?? null,
    workspaceId: meta?.workspaceId ?? input.workspaceId ?? null,
    workspaceName: meta?.workspaceName ?? null,
  }
  await putRecord(record)
  await emitSnapshot()
  return (await processRecord(record)) as MarkEduAttendanceResult | null
}

export const queueMemberHomeworkSubmit = async (
  input: Omit<SubmitEduHomeworkInput, 'fileIds'>,
  files: OutboxHomeworkFile[] = [],
  meta?: { uid?: string | null; workspaceId?: string | null; workspaceName?: string | null; uploadedFileIds?: string[] },
  uploadedFileIds?: string[],
): Promise<SubmitEduHomeworkResult | null> => {
  const finalUploadedIds = uploadedFileIds ?? meta?.uploadedFileIds ?? []
  const now = Date.now()
  const record: MemberOutboxRecord = {
    id: generateId(),
    type: 'EDU_HOMEWORK_SUBMIT',
    eduHomework: {
      input,
      files,
      uploadedFileIds: finalUploadedIds,
    },
    status: 'PENDING',
    retryCount: 0,
    error: null,
    createdAt: now,
    lastUpdatedClient: now,
    uid: meta?.uid ?? null,
    workspaceId: meta?.workspaceId ?? input.workspaceId ?? null,
    workspaceName: meta?.workspaceName ?? null,
  }
  await putRecord(record)
  await emitSnapshot()
  return (await processRecord(record)) as SubmitEduHomeworkResult | null
}

let workerRefCount = 0
let stopWorkerInterval: (() => void) | null = null
let workerOnlineListener: (() => void) | null = null

export const startMemberOutboxWorker = () => {
  workerRefCount++
  if (workerRefCount === 1) {
    const drain = async () => {
      if (workerRefCount === 0) return
      const now = Date.now()
      const records = (await safeListRecords())
        .filter((record) => {
          if (record.terminal) return false
          if (activeProcessingIds.has(record.id)) return false
          if (record.status === 'PENDING') return true
          if (record.status === 'SYNCING' && now - record.lastUpdatedClient > STALE_SYNCING_MS) {
            return true
          }
          return false
        })
        .sort((left, right) => left.createdAt - right.createdAt)
      for (const record of records) {
        if (workerRefCount === 0) break
        if (activeProcessingIds.has(record.id)) continue
        try {
          await processRecord(record)
        } catch {
          // Individual failures are reflected in the outbox snapshot.
        }
      }
    }
    stopWorkerInterval = visibilityAwareInterval(() => void drain(), 15_000)
    workerOnlineListener = () => void drain()
    if (typeof window !== 'undefined') {
      window.addEventListener('online', workerOnlineListener)
    }
    void drain()
  }

  let stopped = false
  return () => {
    if (stopped) return
    stopped = true
    workerRefCount = Math.max(0, workerRefCount - 1)
    if (workerRefCount === 0) {
      if (stopWorkerInterval) {
        stopWorkerInterval()
        stopWorkerInterval = null
      }
      if (workerOnlineListener && typeof window !== 'undefined') {
        window.removeEventListener('online', workerOnlineListener)
        workerOnlineListener = null
      }
    }
  }
}
